export const DEFAULT_SEO_DOMAINS = "www.placement.com,www.exec.com";

export const getConfigDomainWhiteList = () => {
  // For server-side rendering or if global var not defined
  const whitelist =
    (typeof SEO_DOMAIN_WHITELIST === "undefined"
      ? process?.env?.SEO_DOMAIN_WHITELIST || "" // Use process.env if available
      : SEO_DOMAIN_WHITELIST) || DEFAULT_SEO_DOMAINS;

  return (
    whitelist
      ?.split(",")
      .map((domain) => domain.trim())
      .filter((domain) => domain) || []
  );
};

export const ALLOWED_SEO_DOMAINS = getConfigDomainWhiteList();

/**
 * Formats a hostname with proper www prefix if needed
 * @param hostname The hostname to format
 * @returns Formatted hostname with www prefix if needed
 */
export const formatHostname = (hostname: string): string => {
  if (!hostname) return "";

  // Don't modify if already has www. or is a subdomain
  if (hostname.startsWith("www.") || hostname.split(".").length > 2) {
    return hostname;
  }

  // Add www. prefix for root domains
  return `www.${hostname}`;
};

/**
 * Gets the current domain with proper formatting
 * Works in both client-side and server-side rendering environments
 * @param req Optional Express request object for server-side rendering
 * @returns Formatted domain name or empty string if not available
 */
export const getRequestHostDomain = (req?: any) => {
  // Server-side with request object
  if (typeof window === "undefined") {
    if (req?.headers?.host) {
      const hostname = req.headers.host.split(":")[0]; // Remove port if present
      return formatHostname(hostname)?.trim() || "";
    }
    return "";
  }

  // Client-side
  const { hostname } = window.location || {};
  if (!hostname) return "";

  return formatHostname(hostname);
};

export const getCheckDomain = (
  canonicalDomain?: string,
  req?: { headers?: { host?: string } },
): string => {
  if (canonicalDomain) {
    const domainWithoutScheme = canonicalDomain.replace(/^https?:\/\//, "");
    return domainWithoutScheme?.trim() || "";
  }
  return getRequestHostDomain(req);
};

interface SEOWhiteListOptions {
  canonicalDomain?: string;
  req?: { headers?: { host?: string } };
  seoDomainWhiteList?: string[];
}

/**
 * Checks if a domain is in the SEO whitelist
 * This function handles both client-side and server-side rendering scenarios
 *
 * @param options Configuration options containing canonicalDomain, req, and optional seoDomainWhiteList
 * @returns True if domain is in whitelist, false otherwise
 */
export const isSEOWhiteListDomain = ({
  canonicalDomain,
  req,
  seoDomainWhiteList = ALLOWED_SEO_DOMAINS,
}: SEOWhiteListOptions = {}): boolean => {
  const checkDomain = getCheckDomain(canonicalDomain?.trim(), req);

  return checkDomain && seoDomainWhiteList.includes(checkDomain);
};

export const isSeoDisabledFromSetting = (): boolean => {
  if (typeof window === "undefined") {
    return process.env.SEO_DISABLED === "true";
  }
  // Client-side
  return !!SEO_DISABLED;
};
